import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../contexts/AuthContext';
import { useParty } from '../contexts/PartyContext';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { useViewerIdentity } from '../hooks/useViewerIdentity';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { logTelemetryEvent } from '../utils/telemetry';

interface UserSongManagerProps {
  partyId: string;
  isOpen: boolean;
  onClose: () => void;
}

function UserSongManager({ partyId, isOpen, onClose }: UserSongManagerProps) {
  const { user, signInWithGoogle } = useAuth();
  const { songQueue, currentSong, currentParty } = useParty();
  const { viewerId } = useViewerIdentity();
  const { tier } = useSubscription();

  const [processing, setProcessing] = useState(false);
  // Phone mic is handled via the phone-mic search page flow; disable legacy mic in this modal.

  // Legacy mic refs removed

  const removeSongFromQueue = httpsCallable(functions, 'party');

  const mySongs = useMemo(() => {
    const uniqueSongs = new Map<string, typeof songQueue[number]>();

    songQueue.forEach((song) => {
      if (user && song.addedBy === user.uid) {
        uniqueSongs.set(song.id, song);
      } else if (viewerId && song.addedByDeviceId === viewerId) {
        uniqueSongs.set(song.id, song);
      }
    });

    if (currentSong) {
      const matchesUser = user && currentSong.addedBy === user.uid;
      const matchesViewer = viewerId && currentSong.addedByDeviceId === viewerId;
      if (matchesUser || matchesViewer) {
        uniqueSongs.set(currentSong.id, currentSong as typeof songQueue[number]);
      }
    }

    return Array.from(uniqueSongs.values());
  }, [songQueue, user, viewerId, currentSong]);

  const isMyCurrentSong = useMemo(() => {
    if (!currentSong) return false;
    if (user && currentSong.addedBy === user.uid) return true;
    if (viewerId && currentSong.addedByDeviceId === viewerId) return true;
    return false;
  }, [currentSong, user, viewerId]);

  // Only show mic UI if party is mic-enabled
  const micEnabled = !!(currentParty && (currentParty.settings as any)?.phoneMicEnabled);

  const microphoneDocRef = useMemo(() => {
    if (!user) return null;
    return doc(db, 'parties', partyId, 'activeMicrophones', user.uid);
  }, [partyId, user?.uid]);

  // Legacy mic logic removed

  const formatAddedAt = (value: any): string => {
    if (!value) {
      return 'Unknown';
    }

    if (typeof value.toDate === 'function') {
      return value.toDate().toLocaleTimeString();
    }

    if (value instanceof Date) {
      return value.toLocaleTimeString();
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.valueOf())) {
      return parsed.toLocaleTimeString();
    }

    return 'Unknown';
  };

  const handleRemoveSong = async (songId: string) => {
    if (!user) {
      alert('Please sign in to manage your songs.');
      return;
    }
    if (processing) return;

    try {
      setProcessing(true);
      await removeSongFromQueue({
        action: 'queue',
        queueAction: 'remove',
        partyId,
        songId,
        userId: user.uid,
      });
    } catch (error) {
      console.error('Error removing song:', error);
      alert('Failed to remove song from queue.');
    } finally {
      setProcessing(false);
    }
  };

  // Legacy signaling removed

  // Legacy mic claim removed

  // Legacy mic cleanup removed

  // Legacy mic lifecycle removed

  // Legacy mic activation removed; use the phone-mic search page instead.

  // Legacy signaling removed

  // Legacy signaling removed

  // Legacy mic lifecycle removed

  // Legacy mic lifecycle removed

  if (!isOpen) {
    return null;
  }

  // Only allow phone mic when the party supports it and the current user can actually use it.
  const canUseMicrophone = Boolean(micEnabled && user && currentSong && currentSong.addedBy === user.uid && tier !== 'free');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">🎵 My Songs</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {isMyCurrentSong && !user && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">🎶 Your Song Is Playing</h3>
              <p className="text-yellow-700 text-sm">
                Sign in to connect your phone as a wireless microphone and to manage your songs across devices.
              </p>
              <div className="mt-4 flex justify-start">
                <button
                  onClick={() => {
                    void signInWithGoogle();
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  🔑 Sign In with Google
                </button>
              </div>
            </div>
          )}

          {canUseMicrophone && currentSong && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-purple-800 mb-2">🎤 Your Song is Playing!</h3>
                <p className="text-purple-700 text-sm mb-4">
                  To use your phone as a wireless microphone for "{currentSong.title}", open the phone-mic link (QR) and tap "Connect Microphone".
                </p>
                <div className="text-xs text-blue-700 mb-2">Tip: Your mic will remain muted until the host unmutes you or your turn starts.</div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📋 Songs You've Added ({mySongs.length})
            </h3>

            {mySongs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">🎵</div>
                <p>You haven't added any songs yet.</p>
                <p className="text-sm">Scan the QR code or use the search to add songs!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mySongs.map((song) => {
                  const isCurrentlyPlaying = Boolean(currentSong && song.id === currentSong.id);
                  const isQueued = song.status === 'queued';
                  const queueIndex = isQueued ? songQueue.findIndex((qSong) => qSong.id === song.id) : -1;

                  return (
                    <div
                      key={song.id}
                      className={`flex items-center space-x-4 p-4 rounded-lg border ${
                        isCurrentlyPlaying ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {song.thumbnail && (
                        <img src={song.thumbnail} alt={song.title} className="w-16 h-12 object-cover rounded" />
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 truncate">{song.title}</p>
                          {isCurrentlyPlaying && (
                            <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                              NOW PLAYING
                            </span>
                          )}
                          {isQueued && queueIndex >= 0 && (
                            <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                              QUEUED #{queueIndex + 1}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm truncate">{song.artist}</p>
                        <p className="text-gray-500 text-xs">
                          Duration: {song.duration || 'Unknown'} • Added: {formatAddedAt(song.addedAt)}
                        </p>
                      </div>

                      {isQueued && user && (
                        <button
                          onClick={() => handleRemoveSong(song.id)}
                          disabled={processing}
                          className="bg-red-100 hover:bg-red-200 text-red-700 p-2 rounded-lg transition-colors disabled:opacity-50"
                          title="Remove this song"
                        >
                          🗑️
                        </button>
                      )}

                      {isCurrentlyPlaying && <span className="text-green-600 text-2xl">🎤</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {!user && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm">
                <h4 className="font-semibold mb-1">🔐 Sign in to stay in sync</h4>
                <p>Signing in lets you remove songs, resume them on another device, and unlock the microphone when your song plays.</p>
                <button
                  onClick={() => {
                    void signInWithGoogle();
                  }}
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Sign In with Google
                </button>
              </div>
            )}
          </div>

          {processing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm">
              Processing…
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800 space-y-1">
            <h4 className="font-semibold">💡 How It Works</h4>
            <p>• You can only remove songs you added while they're in the queue.</p>
            <p>• Once a song starts playing, it cannot be removed.</p>
            <p>• When your song plays, you can use your phone as a microphone after signing in.</p>
            <p>• Allow microphone permissions when prompted for the best experience.</p>
          </div>
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserSongManager;